import { formatAprValue } from "rujira.ui";
import clsx from "clsx";
import { FC, ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  AssetLabel,
  Button,
  Decimal,
  Fiat,
  classApr,
  formatApr,
  IconDenom,
  Icons,
  Progress,
} from "rujira.ui";
import {
  MobileCardToggle,
  useCardCollapse,
} from "../../../common/components/MobileCard";
import { Graph } from "../../../merge/components/Graph";
import { borrowedUsd, capPercent } from "../../markets/derive";
import { CapData, VaultNode, VaultStrategy } from "../../markets/types";

const CardStat: FC<{
  label: string;
  align?: "right";
  children: ReactNode;
}> = ({ label, align, children }) => (
  <div className={clsx("flex dir-c gap-0.5", { "ai-e text-right": align })}>
    <span className="mobile-card__label">{label}</span>
    {children}
  </div>
);

const AssetLink: FC<{
  asset: VaultNode["asset"];
  url: string;
  large?: boolean;
}> = ({ asset, url, large }) => (
  <Link
    to={url}
    state={{ from: "lend" }}
    className="market-card__asset flex ai-c no-underline color-white">
    <IconDenom
      denom={asset.metadata.symbol}
      className={large ? "w-4.5" : "w-4"}
    />
    <h3
      className={clsx(
        "condensed fw-400 mb-0 ml-1 nowrap",
        large ? "fs-24 lh-24" : "fs-22 lh-22"
      )}>
      <AssetLabel
        asset={asset}
        Container={({ children }) => (
          <small className="color-grey fs-12">{children}</small>
        )}
      />
    </h3>
  </Link>
);

const UtilizationRing: FC<{ ratio: bigint }> = ({ ratio }) => (
  <div className="lending__graph-container">
    <Graph percentage={Number(ratio) / 10_000_000_000} width={50} />
    <span className="lending__graph-container-pct">
      {(Number(ratio) / 1e10).toLocaleDecimal(0)}%
    </span>
  </div>
);

export const MarketCard: FC<{
  vault: VaultNode;
  cap: CapData | undefined;
  myStrategy: VaultStrategy | undefined;
  hasBalance: boolean;
  url: string;
  onDeposit: () => void;
  onWithdraw: () => void;
}> = ({ vault, cap, myStrategy, hasBalance, url, onDeposit, onWithdraw }) => {
  const { collapsed, toggle } = useCardCollapse();
  const { asset, status } = vault;
  const myDeposit = myStrategy?.receiptValue?.amount ?? 0n;
  const capPct = capPercent(cap);

  return (
    <li className="mobile-card market-card">
      <div className={collapsed ? "market-card__summary" : "market-card__head"}>
        <AssetLink asset={asset} url={url} />
        {collapsed ? (
          <div className="flex dir-c ai-e gap-0.5">
            <span className="mobile-card__label">Lending APR</span>
            <span className={`market-card__apr mono ${classApr(status.apr)}`}>
              {formatApr(status.apr)}
            </span>
          </div>
        ) : (
          <>
            <UtilizationRing ratio={status.utilizationRatio} />
            <span className="mobile-card__label market-card__head-caption">
              Utilization
            </span>
          </>
        )}
      </div>

      {!collapsed && (
        <>
          {cap && (
            <div className="mobile-card__panel market-card__cap">
              <div className="market-card__row">
                <span className="mobile-card__label">Borrow Cap</span>
                <Decimal
                  amount={cap.limit}
                  symbol={asset.metadata.symbol}
                  decimals={asset.metadata.decimals}
                  className="fs-14 condensed fw-500"
                  clip
                />
              </div>
              <Progress percentage={capPct} height={3} className="mt-1" />
              <div className="market-card__row mt-0.5">
                <Decimal
                  amount={cap.current}
                  symbol={asset.metadata.symbol}
                  decimals={asset.metadata.decimals}
                  className="fs-12 condensed fw-500 color-grey"
                  clip
                />
                <span className="fs-12 mono color-grey">
                  {capPct.toLocaleDecimal(0)}%
                </span>
              </div>
            </div>
          )}

          <div className="mobile-card__panel market-card__rates">
            <CardStat label="Lending APR">
              <span className={`market-card__apr mono ${classApr(status.apr)}`}>
                {formatApr(status.apr)}
              </span>
            </CardStat>
            <CardStat label="Borrow APR" align="right">
              <span className="fs-16 mono color-white">
                {formatAprValue(status.debtRate)}
              </span>
            </CardStat>
          </div>

          <div className="mobile-card__panel market-card__pools">
            <CardStat label="Total Deposits">
              <Decimal
                amount={status.depositPool.size}
                symbol={asset.metadata.symbol}
                decimals={asset.metadata.decimals}
                className="fs-16 condensed fw-500"
                clip
              />
              <Fiat
                amount={status.valueUsd}
                symbol="$"
                decimals={8}
                className="color-grey fs-12 fw-500 condensed"
              />
            </CardStat>
            <CardStat label="Total Borrowed" align="right">
              <Decimal
                amount={status.debtPool.size}
                symbol={asset.metadata.symbol}
                decimals={asset.metadata.decimals}
                className="fs-16 condensed fw-500"
                clip
              />
              <Fiat
                amount={borrowedUsd(status)}
                symbol="$"
                decimals={8}
                className="color-grey fs-12 fw-500 condensed"
              />
            </CardStat>
          </div>

          {myDeposit > 0n && (
            <div className="mobile-card__panel market-card__position">
              <div className="market-card__row market-card__row--center">
                <span className="mobile-card__label mobile-card__label--lg">
                  My Balance
                </span>
                <div className="flex dir-c ai-e">
                  <Decimal
                    amount={myDeposit}
                    symbol={
                      myStrategy?.receiptValue?.asset.metadata.symbol ||
                      asset.metadata.symbol
                    }
                    decimals={
                      myStrategy?.receiptValue?.asset.metadata.decimals ??
                      asset.metadata.decimals
                    }
                    className="fs-16 condensed fw-500"
                    clip
                  />
                  <Fiat
                    amount={myStrategy?.valueUsd ?? 0n}
                    symbol="$"
                    decimals={8}
                    className="color-grey fs-12 fw-500 condensed"
                  />
                </div>
              </div>
            </div>
          )}
        </>
      )}

      <div className="mobile-card__footer">
        <div className="mobile-card__actions">
          {myDeposit > 0n && (
            <Button
              className="button--sm button--outline button--blue w-full"
              label="Withdraw"
              onClick={onWithdraw}
            />
          )}
          <Button
            className={clsx("button--sm w-full", {
              "button--outline": !hasBalance,
            })}
            disabled={!hasBalance}
            label="Deposit"
            onClick={onDeposit}
          />
        </div>
        {!collapsed && (
          <Link
            to={url}
            state={{ from: "lend" }}
            className="button button--sm button--outline button--grey w-full button--icon-right">
            Details
            <Icons.ArrowUpRight />
          </Link>
        )}
      </div>

      <MobileCardToggle collapsed={collapsed} onToggle={toggle} />
    </li>
  );
};
